import { c as _c } from "react/compiler-runtime";
import { blocosAuditoriaDestinatario, blocosConversasWhatsapp, blocosReplicaEmail, EmailDestinatarios, MarcadorSecao, PainelComprovacao, SecaoAuditoriaIntro, SecaoAutenticidade, SecaoCapa, SecaoEncerramento, SecaoEventosIntro, SecaoObjetivo, SecaoOQueEDocumento, SecaoPartesIntro, SecaoSeguranca, SecaoTextosIntro, SumarioCertificado, TabelaAcessosDestinatario, TabelaPartesGrupo, TituloAcessosPlataforma, TituloCanalAuditoria } from '@/features/arbitral-detalhes';
import { DocumentoPaginado, type BlocoDocumento } from '@/shared/components/documentos/paginado';
import type { CertificadoAcessosSentenca } from '../../../types/certificadoAcessosSentenca.types';
import { SecaoVaziaCertificado } from './SecaoVaziaCertificado';
import { CANAIS_COMPROVACAO_SENTENCA, SECOES_CERTIFICADO_SENTENCA, TEXTOS_CERTIFICADO_SENTENCA, textoOQueEDocumentoSentenca, TITULO_CERTIFICADO_SENTENCA } from './secoesCertificadoSentenca';
function marcador(numero: number): BlocoDocumento {
  return {
    id: `marcador-${numero}`,
    manterComProximo: true,
    node: <MarcadorSecao numero={numero} secoes={SECOES_CERTIFICADO_SENTENCA} />
  };
}
function blocosCapa(certificado: CertificadoAcessosSentenca): BlocoDocumento[] {
  return [{
    id: 'capa',
    manterComProximo: true,
    node: <SecaoCapa certificado={certificado} eyebrow={TEXTOS_CERTIFICADO_SENTENCA.eyebrowCapa} titulo={TITULO_CERTIFICADO_SENTENCA} />
  }, {
    id: 'sumario',
    manterComProximo: true,
    node: <SumarioCertificado secoes={SECOES_CERTIFICADO_SENTENCA} />
  }, {
    id: 'autenticidade',
    node: <SecaoAutenticidade codigoAutenticacao={certificado.codigoAutenticacao} urlAutenticacao={certificado.urlAutenticacao} urlAutenticacaoExibicao={certificado.urlAutenticacaoExibicao} />
  }];
}
function blocosPartes(certificado: CertificadoAcessosSentenca): BlocoDocumento[] {
  return [marcador(1), {
    id: 'partes-intro',
    manterComProximo: true,
    node: <SecaoPartesIntro />
  }, ...certificado.gruposPartes.map(grupo => ({
    id: `partes-${grupo.titulo}`,
    node: <TabelaPartesGrupo grupo={grupo} />
  }))];
}
function blocosObjetivo(certificado: CertificadoAcessosSentenca): BlocoDocumento[] {
  return [marcador(2), {
    id: 'o-que-e-documento',
    node: <SecaoOQueEDocumento numeroProcesso={certificado.numeroProcesso} texto={textoOQueEDocumentoSentenca(certificado.numeroProcesso)} />
  }, {
    id: 'objetivo',
    node: <SecaoObjetivo paragrafos={certificado.objetivoParagrafos} titulo={TEXTOS_CERTIFICADO_SENTENCA.objetivoTitulo} />
  }];
}
function blocosComprovacao(certificado: CertificadoAcessosSentenca): BlocoDocumento[] {
  return [marcador(3), {
    id: 'eventos-intro',
    manterComProximo: true,
    node: <SecaoEventosIntro texto={TEXTOS_CERTIFICADO_SENTENCA.eventosIntro} />
  }, {
    id: 'resumo-comprovacao',
    node: <PainelComprovacao canais={CANAIS_COMPROVACAO_SENTENCA} resumo={certificado.resumoComprovacao} tituloColunaParte={TEXTOS_CERTIFICADO_SENTENCA.comprovacaoColunaParte} />
  }];
}
function blocosSeguranca(certificado: CertificadoAcessosSentenca): BlocoDocumento[] {
  return [marcador(4), {
    id: 'seguranca',
    node: <SecaoSeguranca cadeiaAdicional={certificado.cadeiaDasComunicacoes && {
      ...certificado.cadeiaDasComunicacoes,
      rotulo: ' das comunicações'
    }} cadeiaIntegra={certificado.cadeiaIntegra} cadeiaQuebradaNaVersao={certificado.cadeiaQuebradaNaVersao} hashCertificado={certificado.hashCertificado} paragrafos={certificado.textosSeguranca} versaoHash={certificado.versaoHash} />
  }];
}
function blocosAuditoriaPorCanal(certificado: CertificadoAcessosSentenca): BlocoDocumento[] {
  if (certificado.canaisAuditoria.length === 0) {
    return [{
      id: 'auditoria-vazio',
      node: <SecaoVaziaCertificado mensagem={TEXTOS_CERTIFICADO_SENTENCA.vazioAuditoria} />
    }];
  }
  return certificado.canaisAuditoria.flatMap(canal => [{
    id: `auditoria-titulo-${canal.canal}`,
    manterComProximo: true,
    node: <TituloCanalAuditoria canal={canal.canal} />
  }, ...canal.destinatarios.flatMap((destinatario, index) => blocosAuditoriaDestinatario(canal, destinatario, `auditoria-${canal.canal}-${index}`))]);
}
function blocosAcessosPlataforma(certificado: CertificadoAcessosSentenca): BlocoDocumento[] {
  const titulo: BlocoDocumento = {
    id: 'acessos-titulo',
    manterComProximo: true,
    node: <TituloAcessosPlataforma />
  };
  if (certificado.acessosPlataforma.length === 0) {
    return [titulo, {
      id: 'acessos-vazio',
      node: <SecaoVaziaCertificado mensagem={TEXTOS_CERTIFICADO_SENTENCA.vazioAcessosPlataforma} />
    }];
  }
  return [titulo, ...certificado.acessosPlataforma.map((destinatario, index) => ({
    id: `acessos-${index}`,
    node: <TabelaAcessosDestinatario destinatario={destinatario} />
  }))];
}
function blocosAuditoria(certificado: CertificadoAcessosSentenca): BlocoDocumento[] {
  return [marcador(5), {
    id: 'auditoria-intro',
    manterComProximo: true,
    node: <SecaoAuditoriaIntro textoProvedores={TEXTOS_CERTIFICADO_SENTENCA.auditoriaProvedores} />
  }, ...blocosAuditoriaPorCanal(certificado), ...blocosAcessosPlataforma(certificado)];
}

/**
 * Seção 6 — "Cópia íntegra da intimação enviada às partes".
 *
 * Os DOIS canais entram, como no certificado do processo: a intimação de sentença sai por WhatsApp
 * com frequência — é o caminho da Lexi, que não cria envio digital de e-mail nenhum. Lendo só
 * `mensagensEmail`, um processo intimado apenas por WhatsApp imprimia "nenhuma cópia disponível"
 * nesta seção enquanto as seções 3 e 5 certificavam a entrega pelo canal: o documento negava a
 * cópia de uma comunicação cuja entrega ele mesmo prova, na mesma página.
 */
function blocosComunicados(certificado: CertificadoAcessosSentenca): BlocoDocumento[] {
  if (certificado.mensagensWhatsapp.length === 0 && certificado.mensagensEmail.length === 0) {
    return [marcador(6), {
      id: 'comunicados-vazio',
      node: <SecaoVaziaCertificado mensagem={TEXTOS_CERTIFICADO_SENTENCA.vazioComunicados} />
    }];
  }
  return [marcador(6), {
    id: 'comunicados-intro',
    manterComProximo: true,
    node: <SecaoTextosIntro texto={TEXTOS_CERTIFICADO_SENTENCA.comunicadosIntro} />
  }, ...blocosConversasWhatsapp(certificado.mensagensWhatsapp, 'conversa-whatsapp'), ...certificado.mensagensEmail.flatMap((mensagem, index) => [{
    id: `email-destinatarios-${index}`,
    manterComProximo: true,
    node: <EmailDestinatarios mensagem={mensagem} />
  }, ...blocosReplicaEmail(mensagem, `replica-email-${index}`)])];
}
function construirBlocos(certificado: CertificadoAcessosSentenca): BlocoDocumento[] {
  return [...blocosCapa(certificado), ...blocosPartes(certificado), ...blocosObjetivo(certificado), ...blocosComprovacao(certificado), ...blocosSeguranca(certificado), ...blocosAuditoria(certificado), ...blocosComunicados(certificado), {
    id: 'encerramento',
    node: <SecaoEncerramento certificado={certificado} />
  }];
}
interface CertificadoAcessosSentencaDocumentoProps {
  certificado: CertificadoAcessosSentenca;
}
export function CertificadoAcessosSentencaDocumento(t0) {
  const $ = _c(6);
  const {
    certificado
  } = t0;
  let t1;
  if ($[0] !== certificado) {
    t1 = construirBlocos(certificado);
    $[0] = certificado;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const blocos = t1;
  let t2;
  if ($[2] !== blocos || $[3] !== certificado.codigoAutenticacao || $[4] !== certificado.urlAutenticacaoExibicao) {
    t2 = <DocumentoPaginado blocos={blocos} codigoAutenticacao={certificado.codigoAutenticacao} urlAutenticacaoExibicao={certificado.urlAutenticacaoExibicao} />;
    $[2] = blocos;
    $[3] = certificado.codigoAutenticacao;
    $[4] = certificado.urlAutenticacaoExibicao;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
}